/**
 * Donation progress timeline.
 *
 * Renders whatever steps the server returns rather than a hardcoded list, so
 * the phone and desktop views cannot disagree the way the artwork did — the
 * desktop tracker was missing "Ready for Distribution" entirely (gap G-24).
 */

import { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import { formatDateTime, type TrackingStep } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { CheckIcon } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface TrackingTimelineProps {
  steps: TrackingStep[];
  testID?: string;
}

export const TrackingTimeline = memo(function TrackingTimeline({
  steps,
  testID = 'tracking-timeline',
}: TrackingTimelineProps) {
  const t = useT();
  return (
    <View testID={testID}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <View key={step.status} style={styles.row}>
            <View style={styles.gutter}>
              <StepMarker state={step.state} />
              {!isLast ? (
                <View
                  style={[styles.line, step.state === 'complete' && styles.lineDone]}
                />
              ) : null}
            </View>

            <View style={[styles.content, isLast && styles.contentLast]}>
              <Text
                variant={step.state === 'current' ? 'bodyStrong' : 'body'}
                tone={
                  step.state === 'current'
                    ? 'brand'
                    : step.state === 'complete'
                      ? 'primary'
                      : 'tertiary'
                }
              >
                {t(step.label)}
              </Text>
              {step.occurredAt ? (
                <Text variant="caption" tone="tertiary">
                  {formatDateTime(step.occurredAt)}
                </Text>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
});

const StepMarker = memo(function StepMarker({ state }: { state: TrackingStep['state'] }) {
  if (state === 'complete') {
    return <CheckIcon size={24} color={colors.bg.brand} testID="step-complete" />;
  }
  if (state === 'current') {
    return (
      <View style={styles.currentRing} testID="step-current">
        <View style={styles.currentDot} />
      </View>
    );
  }
  return <View style={styles.pending} testID="step-pending" />;
});

const MARKER = 24;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: space.md,
  },
  gutter: {
    alignItems: 'center',
    width: MARKER,
  },
  line: {
    flex: 1,
    width: 2,
    backgroundColor: colors.border.default,
    marginVertical: space.xxs,
  },
  lineDone: {
    backgroundColor: colors.bg.brand,
  },
  content: {
    flex: 1,
    paddingBottom: space.xl,
    gap: space.xxs,
  },
  contentLast: {
    paddingBottom: 0,
  },
  currentRing: {
    width: MARKER,
    height: MARKER,
    borderRadius: radius.full,
    borderWidth: 2,
    borderColor: colors.bg.brand,
    alignItems: 'center',
    justifyContent: 'center',
  },
  currentDot: {
    width: 10,
    height: 10,
    borderRadius: radius.full,
    backgroundColor: colors.bg.brand,
  },
  pending: {
    width: MARKER,
    height: MARKER,
    borderRadius: radius.full,
    borderWidth: 1.5,
    borderColor: colors.border.strong,
    backgroundColor: colors.bg.surface,
  },
});
